import { useCallback } from 'react';

import {
  type ActivatePeripheralsRequestDto,
  useRetryActivatePeripheralsMutation,
} from '~/api/generated-api';

export type RetryActivateParams = {
  /** Org that owns the peripherals; sent as the `x-bd-organization` header. */
  orgId: string;
  /** Optional fleet scope; sent as the `x-bd-fleet` header when present. */
  fleetId?: string;
  /** Peripheral ids to retry activation for. */
  peripheralIds: string[];
  /** Peripheral type of the ids (e.g. "fleet-camera" / "adapter"). */
  peripheralType: ActivatePeripheralsRequestDto['peripheralType'];
};

/**
 * Result of a single retry-activate call, normalized so callers don't have to
 * repeat the `'error' in result || !success` parsing. A `success` outcome may
 * still omit `batchId` when the backend activated synchronously (no batch to
 * poll), which callers handle differently (register a batch vs. refresh only).
 */
export type RetryActivateOutcome =
  | { status: 'error' }
  | { status: 'success'; batchId?: string; orgId?: string };

export type UseRetryActivatePeripheralsResult = {
  submit: (params: RetryActivateParams) => Promise<RetryActivateOutcome>;
};

/**
 * Shared core for retrying peripheral activation. Builds the request headers +
 * body, calls the `retryActivatePeripherals` mutation, and normalizes the
 * response into a {@link RetryActivateOutcome}.
 *
 * Intentionally does NOT show toasts or dispatch to the activation-progress
 * store: the row-level retry ({@link useRetryPeripheral}) toasts per row and
 * registers via `addActivationBatch`, while the bulk completion-card retry
 * (`useRetryActivationBatch`) toasts once per group and registers via
 * `replaceRetriedBatches`. Both build on this primitive.
 */
export const useRetryActivatePeripherals =
  (): UseRetryActivatePeripheralsResult => {
    const [retryActivatePeripherals] = useRetryActivatePeripheralsMutation();

    const submit = useCallback(
      async ({
        orgId,
        fleetId,
        peripheralIds,
        peripheralType,
      }: RetryActivateParams): Promise<RetryActivateOutcome> => {
        const result = await retryActivatePeripherals({
          'x-bd-organization': orgId,
          ...(fleetId && { 'x-bd-fleet': fleetId }),
          activatePeripheralsRequestDto: { peripheralIds, peripheralType },
        });

        if ('error' in result || result.data?.success === false) {
          return { status: 'error' };
        }

        return {
          status: 'success',
          batchId: result.data?.result?.batchId,
          orgId: result.data?.result?.organizationId,
        };
      },
      [retryActivatePeripherals]
    );

    return { submit };
  };
